import React, { useState, useEffect } from "react";
import { loginConfig as defaultConfig } from "../config/loginConfig";
import axiosInstance, { LEGAL_URLS } from "../utils/axiosConfig";
import { loginWithToken } from "../utils/authHelpers";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { MdOutlineEdit } from "react-icons/md";
import "./Login.css";

const Login = ({ config = defaultConfig }) => {
  const [formData, setFormData] = useState({});
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);

  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [fpStep, setFpStep] = useState(1); // 1 = email, 2 = otp, 3 = new password
  const [fpEmail, setFpEmail] = useState("");
  const [fpOtp, setFpOtp] = useState("");
  const [fpNewPassword, setFpNewPassword] = useState("");
  const [fpConfirmPassword, setFpConfirmPassword] = useState("");
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState("");
  const [fpSuccess, setFpSuccess] = useState("");

  const [visible, setVisible] = useState(false);
  const inputType = visible ? "text" : "password";

  const navigate = useNavigate();

  // Auto-login when redirected with ?token=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const role = params.get("role") || "Client";
    const userId = params.get("userId") || "";
    const nextStep = params.get("next_step");

    if (token) {
      loginWithToken(
        { token, user: { role, id: userId }, next_step: nextStep },
        navigate
      );
    }
  }, [navigate]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoginError("");
    setLoading(true);
    try {
      const response = await axiosInstance.post(`accounts/api/login/`, formData);
      loginWithToken(response.data, navigate);
    } catch (error) {
      const errData = error?.response?.data;
      if (errData && typeof errData === "object") {
        const firstMsg = Object.values(errData)[0];
        setLoginError(typeof firstMsg === "string" ? firstMsg : "Invalid credentials");
      } else {
        setLoginError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 1 — Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setFpError("");
    setFpSuccess("");
    if (!fpEmail) {
      setFpError("Please enter your email.");
      return;
    }
    setFpLoading(true);
    try {
      await axiosInstance.post("stores/reset_request/", { email: fpEmail });
      setFpSuccess("OTP sent! Check your email.");
      setFpStep(2);
    } catch (error) {
      const errData = error?.response?.data;
      if (errData && typeof errData === "object") {
        const firstMsg = Object.values(errData)[0];
        setFpError(typeof firstMsg === "string" ? firstMsg : "Failed to send OTP.");
      } else {
        setFpError("Something went wrong. Please try again.");
      }
    } finally {
      setFpLoading(false);
    }
  };

  // Step 2 — Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setFpError("");
    setFpSuccess("");
    if (!fpOtp) {
      setFpError("Please enter the OTP.");
      return;
    }
    setFpLoading(true);
    try {
      await axiosInstance.post("stores/otp_verifivation/", { email: fpEmail, otp: fpOtp });
      setFpSuccess("OTP verified successfully!");
      setFpStep(3);
    } catch (error) {
      const errData = error?.response?.data;
      if (errData && typeof errData === "object") {
        const firstMsg = Object.values(errData)[0];
        setFpError(typeof firstMsg === "string" ? firstMsg : "Invalid OTP.");
      } else {
        setFpError("Something went wrong. Please try again.");
      }
    } finally {
      setFpLoading(false);
    }
  };

  const resetForgotPasswordFlow = () => {
    setShowForgotPassword(false);
    setFpStep(1);
    setFpEmail("");
    setFpOtp("");
    setFpNewPassword("");
    setFpConfirmPassword("");
    setFpError("");
    setFpSuccess("");
  };

  const handleSavePassword = (e) => {
    e.preventDefault();
    setFpError("");
    setFpSuccess("");

    if (!fpNewPassword || !fpConfirmPassword) {
      setFpError("Please fill in both password fields.");
      return;
    }
    if (fpNewPassword !== fpConfirmPassword) {
      setFpError("Passwords do not match.");
      return;
    }

    resetForgotPasswordFlow();
  };

  const alertMsg = loginError || fpError || fpSuccess;
  const isSuccess = !!fpSuccess;

  return (
    <div className="login-main">
      <div className="login-card">

        {/* Brand */}
        <div className="login-brand">
          <div className="login-brand-title">
            {document.title}
          </div>
        </div>

        {/* Heading */}
        <h2 className="login-heading">
          {showForgotPassword ? "Forgot Password" : "Sign in"}
        </h2>
        <p className="login-subheading">
          {showForgotPassword
            ? fpStep === 1
              ? "Enter your email to receive an OTP."
              : fpStep === 2
              ? "Enter the OTP sent to your email."
              : "Set your new password."
            : "Enter your credentials to access the dashboard."}
        </p>

        {/* Alert */}
        {alertMsg && (
          <div className={`login-alert ${isSuccess ? "success" : "error"}`}>
            {alertMsg}
          </div>
        )}

        {/* ── Forgot Password Flow ── */}
        {showForgotPassword ? (
          <form>
            {fpStep === 1 && (
              <>
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Enter your email"
                    value={fpEmail}
                    onChange={(e) => setFpEmail(e.target.value)}
                    disabled={fpLoading}
                  />
                </div>
                <button
                  type="submit"
                  className="btn-login"
                  onClick={handleSendOtp}
                  disabled={fpLoading}
                >
                  {fpLoading ? <CircularProgress size={20} color="inherit" /> : "Get OTP"}
                </button>
              </>
            )}

            {fpStep === 2 && (
              <>
                <div className="form-group">
                  <label>Email Address</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="email"
                      className="form-control"
                      value={fpEmail}
                      disabled
                    />
                    <span
                      className="edit-icon"
                      onClick={() => {
                        setFpStep(1);
                        setFpOtp("");
                        setFpError("");
                        setFpSuccess("");
                      }}
                    >
                      <MdOutlineEdit />
                    </span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Enter OTP</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="6-digit OTP"
                    maxLength={6}
                    value={fpOtp}
                    onChange={(e) => setFpOtp(e.target.value)}
                    disabled={fpLoading}
                  />
                </div>
                <button
                  type="submit"
                  className="btn-login"
                  onClick={handleVerifyOtp}
                  disabled={fpLoading}
                >
                  {fpLoading ? <CircularProgress size={20} color="inherit" /> : "Verify OTP"}
                </button>
                <span
                  className="text-link"
                  onClick={() => {
                    setFpStep(1);
                    setFpOtp("");
                    setFpError("");
                    setFpSuccess("");
                  }}
                >
                  Resend OTP
                </span>
              </>
            )}

            {fpStep === 3 && (
              <>
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Enter new password"
                    value={fpNewPassword}
                    onChange={(e) => setFpNewPassword(e.target.value)}
                    disabled={fpLoading}
                  />
                </div>
                <div className="form-group has-icon">
                  <label>Confirm Password</label>
                  <input
                    type={inputType}
                    className="form-control"
                    placeholder="Confirm new password"
                    value={fpConfirmPassword}
                    onChange={(e) => setFpConfirmPassword(e.target.value)}
                    disabled={fpLoading}
                  />
                  {visible ? (
                    <img
                      className="inputicon1"
                      src={require("./images/show.png")}
                      alt="hide password"
                      onClick={() => setVisible(false)}
                    />
                  ) : (
                    <img
                      className="inputicon"
                      src={require("./images/eye.jpg")}
                      alt="show password"
                      onClick={() => setVisible(true)}
                    />
                  )}
                </div>
                <button
                  type="submit"
                  className="btn-login"
                  onClick={handleSavePassword}
                  disabled={fpLoading}
                >
                  Save Password
                </button>
              </>
            )}

            <span className="text-link" onClick={resetForgotPasswordFlow}>
              ← Back to Login
            </span>
          </form>
        ) : (
          /* ── Login Form ── */
          <form onSubmit={handleSubmit}>
            {config.sections.map((section) => (
              <React.Fragment key={section.title}>
                {section.fields.map((field) => (
                  <div
                    className={field.name === "password" ? "form-group has-icon" : "form-group"}
                    key={field.name}
                  >
                    <label>{field.label}</label>
                    <input
                      type={field.name === "password" ? inputType : field.type || "text"}
                      className="form-control"
                      placeholder={field.placeholder || ""}
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={handleFieldChange}
                    />
                    {field.name === "password" && (
                      visible ? (
                        <img
                          className="inputicon1"
                          src={require("./images/show.png")}
                          alt="hide password"
                          onClick={() => setVisible(false)}
                        />
                      ) : (
                        <img
                          className="inputicon"
                          src={require("./images/eye.jpg")}
                          alt="show password"
                          onClick={() => setVisible(true)}
                        />
                      )
                    )}
                  </div>
                ))}
              </React.Fragment>
            ))}

            <div className="login-consent-group">
              <label className="login-consent-label">
                <input
                  type="checkbox"
                  className="login-consent-checkbox"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                />
                <span>
                  By checking this box, you consent to receive SMS text messages from RentExcel for account verification, login authentication, and security notifications. Message frequency varies. Msg & data rates may apply. Reply STOP to cancel, HELP for help. Consent is not a condition of purchase or use. See our{" "}
                  <a
                    href={LEGAL_URLS.privacyPolicy}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="login-legal-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href={LEGAL_URLS.termsOfService}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="login-legal-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Use
                  </a>
                  .
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="btn-login"
              disabled={loading || !smsConsent}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Login"}
            </button>

            <span
              className="forgot-link"
              onClick={() => {
                setLoginError("");
                setShowForgotPassword(true);
              }}
            >
              Forgot Password?
            </span>
          </form>
        )}

       </div>
    </div>
  );
};

export { Login };